import { Math as PhaserMath } from 'phaser';
import { ARENA_HEIGHT, ARENA_WIDTH, MAX_RADIUS } from '../game/asteroid-field';
import { getCharacter } from '../game/characters';
import { INITIALS_LENGTH } from '../game/highscores';
import {
  hitRadiusFor,
  NO_INPUT,
  RACER_MAX_X,
  type MoveIntent,
} from '../game/racer-movement';
import {
  advanceRace,
  countdownLabel,
  scoreFor,
  winner,
  type RaceInput,
  type RaceState,
} from '../game/race-state';
import {
  mergeIntent,
  STICK_FULL_DEFLECTION,
  stickOffset,
  touchIntent,
  touchZone,
  type StickReading,
} from '../game/touch-input';
import { exhaustScale } from '../game/exhaust';
import { KoodooScene, TEXT_STYLES } from './base-scene';

export const RACE_TRACK = 'killerrobots.mp3';

const ASTEROID_TEXTURE = 'asteroid_rock';
const TRAIL_TEXTURE = 'asteroid_trail';
const SPARK_TEXTURE = 'race_spark';
const ASTEROID_POINTS = 11;

/** Trail length as a multiple of the rock's diameter. */
const TRAIL_LENGTH_FACTOR = 5.5;

const PLAYER_TINT = { 1: 0xff6b6b, 2: 0x63b3ff } as const;

/**
 * The racer's craft, lifted from the original version-one artwork
 * (`player1.png`) with the canopy and head cropped away, so one hull serves
 * every character.
 */
const SHIP_TEXTURE = 'racer_ship';
/** Native size of that crop, used to keep its proportions honest. */
const SHIP_SOURCE_WIDTH = 1096;
const SHIP_SOURCE_HEIGHT = 227;
const SHIP_ASPECT = SHIP_SOURCE_HEIGHT / SHIP_SOURCE_WIDTH;
/** Ship width relative to the racer's head. */
const SHIP_WIDTH_FACTOR = 2.9;
/**
 * Where the head sits along the hull, as a fraction of its width — measured
 * from where the canopy was in the source art, so the head lands in the
 * cockpit rather than floating over the middle of the ship.
 */
const SHIP_HEAD_ANCHOR_X = 0.6925;
/** How far the hull is tucked under the head, as a fraction of head size. */
const SHIP_DROP = 0.22;

/**
 * Where the flame ends and the hull begins in the source art, in source pixels.
 *
 * The exhaust was painted into the same file, so it could never vary in width
 * while the whole ship was one sprite. The scene cuts the texture into two
 * frames instead of shipping new artwork. See docs/exhaust-flame.md.
 */
const FLAME_CUT_X = 398;
const HULL_FRAME = 'hull';
const FLAME_FRAME = 'flame';

/** The head anchor in source pixels, which both frames are measured against. */
const SHIP_ANCHOR_PX = SHIP_HEAD_ANCHOR_X * SHIP_SOURCE_WIDTH;
/** Hull frame width, and its own origin, so it lands where the whole ship did. */
const HULL_WIDTH_FACTOR = (SHIP_SOURCE_WIDTH - FLAME_CUT_X) / SHIP_SOURCE_WIDTH;
const HULL_ORIGIN_X = (SHIP_ANCHOR_PX - FLAME_CUT_X) / (SHIP_SOURCE_WIDTH - FLAME_CUT_X);
/** The nozzle, as a signed fraction of ship width from the head anchor. */
const FLAME_ANCHOR_OFFSET = (FLAME_CUT_X - SHIP_ANCHOR_PX) / SHIP_SOURCE_WIDTH;
/** The flame at its full painted width, relative to the whole ship. */
const FLAME_WIDTH_FACTOR = FLAME_CUT_X / SHIP_SOURCE_WIDTH;
/** Where the nozzle sits down the hull, as a fraction of ship height. */
const NOZZLE_Y = 0.25;

/** Puffs are shed on a clock, so the trail is the same at 60Hz and 144Hz. */
const SMOKE_INTERVAL_MS = 70;
const SMOKE_LIFE_MS = 420;
/** Below this a racer is drifting, not working hard enough to shed anything. */
const SMOKE_MIN_SPEED = 60;
/** Base sprite size as a multiple of the hit radius. */
const RACER_SPRITE_SCALE = 2.4;
/** Plus a flat ten pixels, so the racer reads clearly against the hull. */
const RACER_SPRITE_MARGIN = 10;

/**
 * The race: one shared arena, rocks streaking in from the right, and two
 * racers who can shove each other into them.
 *
 * Every rule lives in `game/race-state.ts` and its collaborators. This scene
 * reads input, calls the reducer, and dresses up whatever comes back.
 */
export class Race extends KoodooScene {
  static readonly KEY = 'Race';

  private state!: RaceState;
  private sprites = new Map<number, Phaser.GameObjects.Image>();
  private trails = new Map<number, Phaser.GameObjects.Image>();
  private racerSprites = new Map<1 | 2, Phaser.GameObjects.Image>();
  private shipSprites = new Map<1 | 2, Phaser.GameObjects.Image>();
  private flameSprites = new Map<1 | 2, Phaser.GameObjects.Image>();
  private smokeElapsed = 0;
  private boostBars = new Map<1 | 2, Phaser.GameObjects.Rectangle>();
  private scoreTexts = new Map<1 | 2, Phaser.GameObjects.Text>();
  private statusText?: Phaser.GameObjects.Text;
  private keys?: Record<string, Phaser.Input.Keyboard.Key>;
  private finished = false;
  private initials = '';
  private initialsText?: Phaser.GameObjects.Text;

  /** Live thumbstick, or null while the steering thumb is lifted. */
  private stick: StickReading | null = null;
  private stickPointer?: number;
  private boostPointer?: number;
  /**
   * Set by the first touch, never by a mouse. Touch-capable laptops are
   * common, so the on-screen controls are earned by use rather than offered
   * because the browser claims support.
   */
  private touchActive = false;
  private stickBase?: Phaser.GameObjects.Arc;
  private stickKnob?: Phaser.GameObjects.Arc;

  constructor() {
    super(Race.KEY);
  }

  preload(): void {
    this.loadBackgrounds(['bg-1-basecolor', 'bg-2-air']);
    this.load.image(SHIP_TEXTURE, 'assets/images/players/racer-ship.png');
    const select = this.session.getSelectState();
    for (const character of Object.values(select.selections)) {
      if (character) {
        this.load.image(character, `assets/images/players/${character}-1x1.png`);
      }
    }
  }

  create(): void {
    // Phaser reuses scene instances, so clear the previous run's objects.
    this.sprites.clear();
    this.trails.clear();
    this.racerSprites.clear();
    this.shipSprites.clear();
    this.flameSprites.clear();
    this.smokeElapsed = 0;
    this.boostBars.clear();
    this.scoreTexts.clear();
    this.finished = false;
    this.initials = '';
    this.initialsText = undefined;
    this.stick = null;
    this.stickPointer = undefined;
    this.boostPointer = undefined;
    this.touchActive = false;

    this.state = this.session.startRace();

    this.makeAsteroidTexture();
    this.makeTrailTexture();
    this.makeSparkTexture();
    this.sliceShipTexture();
    this.drawArena();

    for (const [index, racer] of this.state.racers.entries()) {
      const headSize = this.headSizeFor(racer.character);
      const shipWidth = headSize * SHIP_WIDTH_FACTOR;

      // The flame hangs off the back of the nozzle, so it is anchored by its
      // right edge and drawn under the hull.
      this.flameSprites.set(
        racer.player,
        this.add.image(racer.body.x, racer.body.y, SHIP_TEXTURE, FLAME_FRAME).setOrigin(1, 0).setDepth(8),
      );

      // The hull is drawn first and sits under the head, nose pointing into
      // the oncoming rocks.
      this.shipSprites.set(
        racer.player,
        this.add
          .image(racer.body.x, racer.body.y, SHIP_TEXTURE, HULL_FRAME)
          .setOrigin(HULL_ORIGIN_X, 0)
          .setDisplaySize(shipWidth * HULL_WIDTH_FACTOR, shipWidth * SHIP_ASPECT)
          .setDepth(9),
      );

      this.racerSprites.set(
        racer.player,
        this.add
          .image(racer.body.x, racer.body.y, racer.character)
          .setDisplaySize(headSize, headSize)
          .setDepth(10),
      );

      const top = 20 + index * 34;
      this.scoreTexts.set(
        racer.player,
        this.add.text(28, top, '', { ...TEXT_STYLES.hint, fontSize: '20px' }).setDepth(20),
      );
      this.boostBars.set(
        racer.player,
        this.add
          .rectangle(28, top + 26, 120, 7, PLAYER_TINT[racer.player])
          .setOrigin(0, 0.5)
          .setDepth(20),
      );
    }

    this.statusText = this.add
      .text(ARENA_WIDTH / 2, ARENA_HEIGHT / 2, '', {
        ...TEXT_STYLES.menu,
        fontSize: '68px',
      })
      .setOrigin(0.5)
      .setDepth(30);

    this.setupInput();
    this.createStickVisuals();

    this.session.audio.stopMusic();
    this.session.audio.playMusic(RACE_TRACK, 0.35);

    this.render();
  }

  override update(_time: number, delta: number): void {
    if (this.finished) {
      return;
    }

    const next = advanceRace(this.state, delta, this.readInput());
    if (next === this.state) {
      return;
    }

    this.state = next;
    this.render();
    this.shedSmoke(delta);
    this.playEvents();

    if (next.phase === 'finished') {
      this.finished = true;
      this.onFinished();
    }
  }

  /** Exposed so tests can inspect the live race without scraping pixels. */
  getState(): RaceState {
    return this.state;
  }

  /**
   * Whether a real touch has been seen this round.
   *
   * Drives the on-screen initials keypad, and stays false for a whole
   * mouse-driven session even on a touch-capable laptop.
   */
  isTouchEngaged(): boolean {
    return this.touchActive;
  }

  private readInput(): RaceInput {
    if (this.state.phase !== 'racing') {
      return {};
    }
    const k = this.keys;
    const solo = this.state.mode === 'highscore_race';

    // In single player either control set works, so nobody has to learn WASD.
    const keyboardP1: MoveIntent = k
      ? {
          up: k.w!.isDown || (solo && k.up!.isDown),
          down: k.s!.isDown || (solo && k.down!.isDown),
          left: k.a!.isDown || (solo && k.left!.isDown),
          right: k.d!.isDown || (solo && k.right!.isDown),
          boost: k.boostLeft!.isDown || (solo && k.boostRight!.isDown),
        }
      : NO_INPUT;

    if (solo) {
      // Touch is additive: a device with both can switch mid-round.
      return {
        1: mergeIntent(keyboardP1, touchIntent(this.stick, this.boostPointer !== undefined)),
      };
    }

    if (!k) {
      return {};
    }
    const p2: MoveIntent = {
      up: k.up!.isDown,
      down: k.down!.isDown,
      left: k.left!.isDown,
      right: k.right!.isDown,
      boost: k.boostRight!.isDown,
    };

    return { 1: keyboardP1, 2: p2 };
  }

  /** Turns reducer events into noise, shake and sparks. */
  private playEvents(): void {
    const { wipeouts, nearMisses, bumped } = this.state.events;

    for (const player of wipeouts) {
      const sprite = this.racerSprites.get(player);
      if (sprite) {
        this.burst(sprite.x, sprite.y, 24, PLAYER_TINT[player]);
      }
      this.cameras.main.shake(320, 0.02);
      this.cameras.main.flash(150, 90, 30, 30);
      this.session.audio.playEffect('mouse_click', 0.45);
    }

    if (bumped) {
      this.cameras.main.shake(110, 0.006);
      this.session.audio.playEffect('mouse_click', 0.75);
    }

    for (const player of nearMisses) {
      const sprite = this.racerSprites.get(player);
      if (sprite) {
        this.burst(sprite.x, sprite.y, 5, 0xffffff);
      }
      this.session.audio.playEffect('mouse_over', 1.9);
    }
  }

  /**
   * Cuts the one ship texture into a hull frame and a flame frame.
   *
   * Both come off the image already loaded, so the flame can be scaled on its
   * own without a second asset or a second request.
   */
  private sliceShipTexture(): void {
    const texture = this.textures.get(SHIP_TEXTURE);
    if (texture.has(FLAME_FRAME)) {
      return;
    }
    texture.add(FLAME_FRAME, 0, 0, 0, FLAME_CUT_X, SHIP_SOURCE_HEIGHT);
    texture.add(HULL_FRAME, 0, FLAME_CUT_X, 0, SHIP_SOURCE_WIDTH - FLAME_CUT_X, SHIP_SOURCE_HEIGHT);
  }

  /**
   * Small white puffs shed from the nozzles, gone within half a second.
   *
   * Emitted on a clock rather than once per frame, so a fast display does not
   * lay down a denser trail than a slow one.
   */
  private shedSmoke(deltaMs: number): void {
    this.smokeElapsed += deltaMs;
    if (this.smokeElapsed < SMOKE_INTERVAL_MS) {
      return;
    }
    this.smokeElapsed = 0;

    for (const racer of this.state.racers) {
      const speed = Math.abs(racer.body.vx) + Math.abs(racer.body.vy);
      if (!racer.alive || speed < SMOKE_MIN_SPEED) {
        continue;
      }

      const headSize = this.headSizeFor(racer.character);
      const shipWidth = headSize * SHIP_WIDTH_FACTOR;
      const x = racer.body.x + shipWidth * FLAME_ANCHOR_OFFSET;
      const y = racer.body.y + headSize * SHIP_DROP + shipWidth * SHIP_ASPECT * NOZZLE_Y;

      const puff = this.add
        .image(x + (Math.random() - 0.5) * 10, y + (Math.random() - 0.5) * 10, SPARK_TEXTURE)
        .setDepth(7)
        .setAlpha(0.5)
        .setScale(0.35 + Math.random() * 0.3);

      this.tweens.add({
        targets: puff,
        x: puff.x - 70 - Math.random() * 70,
        y: puff.y + (Math.random() - 0.5) * 28,
        alpha: 0,
        // Puffs spread as they cool, which is what makes them read as smoke
        // rather than as more sparks.
        scale: puff.scale * 2.2,
        duration: SMOKE_LIFE_MS + Math.random() * 140,
        ease: 'Sine.easeOut',
        onComplete: () => puff.destroy(),
      });
    }
  }

  /** A short-lived spray of sparks, used for hits and near misses alike. */
  private burst(x: number, y: number, count: number, tint: number): void {
    for (let i = 0; i < count; i += 1) {
      const angle = Math.random() * Math.PI * 2;
      const distance = 60 + Math.random() * 260;
      const spark = this.add
        .image(x, y, SPARK_TEXTURE)
        .setTint(tint)
        .setDepth(25)
        .setScale(0.4 + Math.random() * 0.8);

      this.tweens.add({
        targets: spark,
        x: x + Math.cos(angle) * distance,
        y: y + Math.sin(angle) * distance,
        alpha: 0,
        scale: 0,
        duration: 320 + Math.random() * 260,
        ease: 'Cubic.easeOut',
        onComplete: () => spark.destroy(),
      });
    }
  }

  private render(): void {
    this.syncAsteroids();
    this.renderStick();

    for (const racer of this.state.racers) {
      const headSize = this.headSizeFor(racer.character);
      const lean = racer.alive ? racer.body.vy / 2600 : 0;

      this.racerSprites
        .get(racer.player)
        ?.setPosition(racer.body.x, racer.body.y)
        .setAlpha(racer.alive ? 1 : 0.25)
        // Lean into the direction of travel; pure decoration.
        .setRotation(lean);

      // The hull follows the head and leans with it. A boosting racer's ship
      // stretches a little, which is the cheapest way to sell the thrust.
      const ship = this.shipSprites.get(racer.player);
      const boosting = racer.alive && racer.body.boost < 1 && racer.body.vx > 40;
      const shipWidth = headSize * SHIP_WIDTH_FACTOR * (boosting ? 1.08 : 1);
      const shipX = racer.body.x;
      const shipY = racer.body.y + headSize * SHIP_DROP;
      ship
        ?.setPosition(shipX, shipY)
        .setDisplaySize(shipWidth * HULL_WIDTH_FACTOR, shipWidth * SHIP_ASPECT)
        .setAlpha(racer.alive ? 1 : 0.25)
        .setRotation(lean);

      // The flame stretches out of the nozzle rather than inflating, so only
      // its width moves. Its anchor sits behind the head and has to lean with
      // the ship, which is what the rotated offset is doing.
      const flameWidth = exhaustScale(racer.body, racer.alive) * shipWidth * FLAME_WIDTH_FACTOR;
      const reach = shipWidth * FLAME_ANCHOR_OFFSET;
      const flame = this.flameSprites.get(racer.player);
      flame?.setVisible(flameWidth > 0);
      if (flame && flameWidth > 0) {
        flame
          .setPosition(shipX + reach * Math.cos(lean), shipY + reach * Math.sin(lean))
          .setDisplaySize(flameWidth, shipWidth * SHIP_ASPECT)
          .setRotation(lean);
      }

      const traits = getCharacter(racer.character);
      this.scoreTexts
        .get(racer.player)
        ?.setText(
          `P${racer.player} ${traits.displayName.toUpperCase()}  ${scoreFor(racer)}` +
            (racer.alive ? '' : '   OUT'),
        );

      const bar = this.boostBars.get(racer.player);
      bar?.setDisplaySize(Math.max(1, 120 * racer.body.boost), 7);
      bar?.setAlpha(racer.alive ? 1 : 0.2);
    }

    this.statusText?.setText(this.statusLabel());
  }

  /**
   * Adds sprites for new rocks, moves existing ones, drops culled ones.
   *
   * Each rock also drags a white trail behind it — anchored at the rock and
   * stretching back to the right, since everything travels leftwards.
   */
  private syncAsteroids(): void {
    const live = new Set<number>();

    for (const asteroid of this.state.field.asteroids) {
      live.add(asteroid.id);

      let trail = this.trails.get(asteroid.id);
      if (!trail) {
        trail = this.add.image(asteroid.x, asteroid.y, TRAIL_TEXTURE).setOrigin(0, 0.5);
        this.trails.set(asteroid.id, trail);
      }
      trail
        .setPosition(asteroid.x, asteroid.y)
        .setDisplaySize(asteroid.radius * TRAIL_LENGTH_FACTOR, asteroid.radius * 1.15)
        .setDepth(1);

      let sprite = this.sprites.get(asteroid.id);
      if (!sprite) {
        sprite = this.add.image(asteroid.x, asteroid.y, ASTEROID_TEXTURE);
        this.sprites.set(asteroid.id, sprite);
      }
      sprite
        .setPosition(asteroid.x, asteroid.y)
        .setDisplaySize(asteroid.radius * 2, asteroid.radius * 2)
        .setRotation(asteroid.rotation)
        .setDepth(2);
    }

    for (const [id, sprite] of this.sprites) {
      if (!live.has(id)) {
        sprite.destroy();
        this.sprites.delete(id);
        this.trails.get(id)?.destroy();
        this.trails.delete(id);
      }
    }
  }

  /**
   * How big a racer is drawn. Larger than the circle it is judged by, so a
   * graze reads as a graze rather than as a hit that was missed.
   */
  private headSizeFor(character: Parameters<typeof hitRadiusFor>[0]): number {
    return hitRadiusFor(character) * RACER_SPRITE_SCALE + RACER_SPRITE_MARGIN;
  }

  private statusLabel(): string {
    switch (this.state.phase) {
      case 'get_ready':
        return 'Get Ready...';
      case 'countdown':
        return countdownLabel(this.state) ?? '';
      default:
        return '';
    }
  }

  private onFinished(): void {
    this.session.audio.stopMusic();

    // The round is over: stop tracking thumbs, and take the stick off screen
    // so it cannot sit under the results.
    this.input.off('pointerdown');
    this.input.off('pointermove');
    this.stick = null;
    this.stickBase?.destroy();
    this.stickKnob?.destroy();
    this.stickBase = undefined;
    this.stickKnob = undefined;

    const champion = winner(this.state);
    const best = Math.max(...this.state.racers.map(scoreFor));
    const headline =
      this.state.mode === 'highscore_race'
        ? `Wiped Out - ${best}`
        : champion
          ? `Player ${champion.player} Wins - ${scoreFor(champion)}`
          : `Dead Heat - ${best}`;

    this.add
      .text(ARENA_WIDTH / 2, ARENA_HEIGHT / 2 - 110, headline, {
        ...TEXT_STYLES.menu,
        fontSize: '52px',
        align: 'center',
      })
      .setOrigin(0.5)
      .setDepth(40);

    if (this.session.qualifiesForBoard(this.state)) {
      this.askForInitials();
      return;
    }

    this.offerExit('click to return to the menu');
  }

  /** Arcade-style three-letter entry, typed straight into the canvas. */
  private askForInitials(): void {
    this.add
      .text(ARENA_WIDTH / 2, ARENA_HEIGHT / 2 - 20, 'HIGH SCORE!  Enter your initials', {
        ...TEXT_STYLES.menu,
        fontSize: '28px',
      })
      .setOrigin(0.5)
      .setDepth(40);

    this.initialsText = this.add
      .text(ARENA_WIDTH / 2, ARENA_HEIGHT / 2 + 55, '_ _ _', {
        ...TEXT_STYLES.menu,
        fontSize: '64px',
      })
      .setOrigin(0.5)
      .setDepth(40);

    const hint = this.add
      .text(
        ARENA_WIDTH / 2,
        ARENA_HEIGHT / 2 + 125,
        this.touchActive ? 'tap 3 letters, then OK' : 'type 3 letters, then Enter',
        {
          ...TEXT_STYLES.hint,
          fontSize: '18px',
        },
      )
      .setOrigin(0.5)
      .setDepth(40);

    if (this.touchActive) {
      this.addInitialsKeypad(hint);
    }

    this.input.keyboard?.on('keydown', (event: KeyboardEvent) => {
      if (event.key === 'Backspace') {
        this.initials = this.initials.slice(0, -1);
      } else if (event.key === 'Enter') {
        if (this.initials.length > 0) {
          this.commitInitials(hint);
        }
        return;
      } else if (
        /^[a-zA-Z0-9]$/.test(event.key) &&
        this.initials.length < INITIALS_LENGTH
      ) {
        this.initials += event.key.toUpperCase();
      }
      this.renderInitials();
    });
  }

  /**
   * An on-screen alphabet for touch players.
   *
   * Without it a phone can play the game but can never post a score, which
   * would make the board a desktop-only club.
   */
  private addInitialsKeypad(hint: Phaser.GameObjects.Text): void {
    const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');
    const columns = 10;
    const cellWidth = 60;
    const cellHeight = 52;
    const top = ARENA_HEIGHT / 2 + 170;
    const left = ARENA_WIDTH / 2 - ((columns - 1) * cellWidth) / 2;

    const key = (label: string, x: number, y: number, onPress: () => void) => {
      const plate = this.add
        .rectangle(x, y, cellWidth - 8, cellHeight - 8, 0xffffff, 0.1)
        .setStrokeStyle(1, 0xffffff, 0.3)
        .setDepth(40)
        .setInteractive();
      this.add
        .text(x, y, label, { ...TEXT_STYLES.menu, fontSize: '22px' })
        .setOrigin(0.5)
        .setDepth(41);

      plate.on('pointerdown', () => {
        plate.setFillStyle(0xffffff, 0.32);
        this.session.audio.playEffect('mouse_click');
        onPress();
        this.renderInitials();
      });
      plate.on('pointerup', () => plate.setFillStyle(0xffffff, 0.1));
      plate.on('pointerout', () => plate.setFillStyle(0xffffff, 0.1));
    };

    for (const [index, letter] of letters.entries()) {
      key(
        letter,
        left + (index % columns) * cellWidth,
        top + Math.floor(index / columns) * cellHeight,
        () => {
          if (this.initials.length < INITIALS_LENGTH) {
            this.initials += letter;
          }
        },
      );
    }

    const lastRow = top + Math.floor(letters.length / columns) * cellHeight;
    key('DEL', left + 7 * cellWidth, lastRow, () => {
      this.initials = this.initials.slice(0, -1);
    });
    key('OK', left + 9 * cellWidth, lastRow, () => {
      if (this.initials.length > 0) {
        this.commitInitials(hint);
      }
    });
  }

  private renderInitials(): void {
    const slots: string[] = [];
    for (let i = 0; i < INITIALS_LENGTH; i += 1) {
      slots.push(this.initials[i] ?? '_');
    }
    this.initialsText?.setText(slots.join(' '));
  }

  private commitInitials(hint: Phaser.GameObjects.Text): void {
    this.session.recordResult(this.state, this.initials);
    this.input.keyboard?.removeAllListeners('keydown');
    hint.setText('saved to the high score board');
    this.time.delayedCall(700, () => this.scene.start('Highscores'));
  }

  private offerExit(label: string): void {
    this.add
      .text(ARENA_WIDTH / 2, ARENA_HEIGHT - 90, label, {
        ...TEXT_STYLES.menu,
        fontSize: '26px',
      })
      .setOrigin(0.5)
      .setDepth(40);

    this.time.delayedCall(600, () => {
      this.input.once('pointerup', () => this.scene.start('MainMenu'));
    });
  }

  private setupInput(): void {
    // Touch is wired first: a handset may have no keyboard plugin at all, and
    // it is the only way to play on one.
    this.setupTouch();

    const keyboard = this.input.keyboard;
    if (!keyboard) {
      return;
    }
    this.keys = {
      w: keyboard.addKey('W'),
      a: keyboard.addKey('A'),
      s: keyboard.addKey('S'),
      d: keyboard.addKey('D'),
      up: keyboard.addKey('UP'),
      down: keyboard.addKey('DOWN'),
      left: keyboard.addKey('LEFT'),
      right: keyboard.addKey('RIGHT'),
      // Shift sits beside WASD; space is reachable for the arrow-keys player.
      boostLeft: keyboard.addKey('SHIFT'),
      boostRight: keyboard.addKey('SPACE'),
    };
  }

  /**
   * A floating thumbstick on the left, boost on the right. See
   * docs/touch-controls.md — the rules live in `game/touch-input.ts`; this
   * only tracks which finger is doing what.
   */
  private setupTouch(): void {
    if (this.state.mode !== 'highscore_race') {
      // Two people sharing one handset is not a mode this game supports.
      return;
    }

    // Phaser tracks a single touch by default; steering and boosting at the
    // same time needs two.
    this.input.addPointer(1);

    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      if (!pointer.wasTouch) {
        return;
      }
      this.touchActive = true;

      if (touchZone(pointer.x) === 'stick') {
        if (this.stickPointer === undefined) {
          this.stickPointer = pointer.id;
          this.stick = {
            originX: pointer.x,
            originY: pointer.y,
            x: pointer.x,
            y: pointer.y,
          };
        }
      } else if (this.boostPointer === undefined) {
        this.boostPointer = pointer.id;
      }
    });

    this.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      if (pointer.id === this.stickPointer && this.stick) {
        this.stick = { ...this.stick, x: pointer.x, y: pointer.y };
      }
    });

    const release = (pointer: Phaser.Input.Pointer) => {
      if (pointer.id === this.stickPointer) {
        this.stickPointer = undefined;
        this.stick = null;
      }
      if (pointer.id === this.boostPointer) {
        this.boostPointer = undefined;
      }
    };
    this.input.on('pointerup', release);
    // A finger dragged off the canvas never sends pointerup, and a stick that
    // stays stuck on would fly the racer into a wall.
    this.input.on('pointerupoutside', release);
    this.input.on('gameout', () => {
      this.stickPointer = undefined;
      this.boostPointer = undefined;
      this.stick = null;
    });
  }

  /**
   * The stick draws *underneath* the rocks and the racers (trails are depth 1,
   * rocks 2, racers 9). A thumb lands wherever it lands, and a control that
   * can hide an incoming rock is worse than one that is briefly hard to see.
   */
  private createStickVisuals(): void {
    this.stickBase = this.add
      .circle(0, 0, STICK_FULL_DEFLECTION, 0xffffff, 0.07)
      .setStrokeStyle(2, 0xffffff, 0.22)
      .setDepth(0.5)
      .setVisible(false);
    this.stickKnob = this.add
      .circle(0, 0, STICK_FULL_DEFLECTION * 0.42, 0xffffff, 0.24)
      .setDepth(0.6)
      .setVisible(false);
  }

  private renderStick(): void {
    const stick = this.stick;
    if (!stick) {
      this.stickBase?.setVisible(false);
      this.stickKnob?.setVisible(false);
      return;
    }

    const offset = stickOffset(stick);
    this.stickBase?.setVisible(true).setPosition(stick.originX, stick.originY);
    this.stickKnob
      ?.setVisible(true)
      .setPosition(stick.originX + offset.x, stick.originY + offset.y);
  }

  /** Backdrop, plus a marker showing how far right a racer may fly. */
  private drawArena(): void {
    this.add
      .image(ARENA_WIDTH / 2, ARENA_HEIGHT / 2, 'bg-1-basecolor')
      .setDisplaySize(ARENA_WIDTH, ARENA_HEIGHT);
    this.add
      .image(ARENA_WIDTH / 2, ARENA_HEIGHT / 2, 'bg-2-air')
      .setDisplaySize(ARENA_WIDTH, ARENA_HEIGHT)
      .setAlpha(0.45);

    this.add
      .rectangle(RACER_MAX_X, ARENA_HEIGHT / 2, 3, ARENA_HEIGHT, 0xffffff)
      .setAlpha(0.09)
      .setDepth(1);
  }

  private makeSparkTexture(): void {
    if (this.textures.exists(SPARK_TEXTURE)) {
      return;
    }
    const graphics = this.add.graphics();
    graphics.fillStyle(0xffffff, 1);
    graphics.fillCircle(6, 6, 6);
    graphics.generateTexture(SPARK_TEXTURE, 12, 12);
    graphics.destroy();
  }

  /**
   * A white streak that fades out behind each rock.
   *
   * Drawn as a strip of vertical slices whose alpha falls off with distance,
   * which is cheaper per frame than a particle emitter and enough to sell the
   * speed.
   */
  private makeTrailTexture(): void {
    if (this.textures.exists(TRAIL_TEXTURE)) {
      return;
    }

    const width = 160;
    const height = 24;
    const graphics = this.add.graphics();

    for (let i = 0; i < width; i += 1) {
      const t = i / width;
      const alpha = (1 - t) ** 2 * 0.55;
      const taper = 1 - t * 0.75;
      graphics.fillStyle(0xffffff, alpha);
      graphics.fillRect(i, (height * (1 - taper)) / 2, 1, height * taper);
    }

    graphics.generateTexture(TRAIL_TEXTURE, width, height);
    graphics.destroy();
  }

  /**
   * Builds a chunky rock polygon at runtime.
   *
   * The project has no asteroid art, and generating it keeps the download
   * unchanged while still giving every rock a believable silhouette.
   */
  private makeAsteroidTexture(): void {
    if (this.textures.exists(ASTEROID_TEXTURE)) {
      return;
    }

    const size = MAX_RADIUS * 2;
    const centre = MAX_RADIUS;
    const graphics = this.add.graphics();
    graphics.fillStyle(0x6b6357, 1);
    graphics.lineStyle(4, 0x3f3a33, 1);

    const points: Phaser.Math.Vector2[] = [];
    for (let i = 0; i < ASTEROID_POINTS; i += 1) {
      const angle = (i / ASTEROID_POINTS) * Math.PI * 2;
      // Deterministic jitter keeps the silhouette irregular but stable.
      const wobble = 0.78 + 0.22 * Math.abs(Math.sin(i * 2.399));
      const radius = (MAX_RADIUS - 3) * wobble;
      points.push(
        new PhaserMath.Vector2(
          centre + Math.cos(angle) * radius,
          centre + Math.sin(angle) * radius,
        ),
      );
    }

    graphics.fillPoints(points, true);
    graphics.strokePoints(points, true);
    graphics.generateTexture(ASTEROID_TEXTURE, size, size);
    graphics.destroy();
  }
}
